import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Home, Building, Building2, Send, ChevronDown, Sparkles } from "lucide-react";
import Reveal from "./Reveal";
import { streamSSE } from "./ChatWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Orientační sazby práce + materiál, střední standard, průměr ČR 2026
// Ceny ALIANCE ŽIVNOSTNÍKŮ — bez korporátních marží stavební firmy (firma účtuje ~30–40 % víc)
const TYPES = [
  { id: "novostavba", label: "Novostavba domu na klíč", rate: 28000, icon: Home },
  { id: "rek-domu", label: "Rekonstrukce domu", rate: 11000, icon: Building },
  { id: "rek-bytu", label: "Rekonstrukce bytu", rate: 10500, icon: Building2 },
];

// Stejná obytná plocha ve více patrech = menší půdorys (základy + střecha) → nižší cena za m²
const FLOORS_COEF = { 1: 1.0, 2: 0.95, 3: 0.91 };
// Obytné podkroví zvedá cenu (šikminy, krokev, složitější práce)
const ATTIC_COEF = 1.1;

// Regionální koeficienty cen stavebních prací (průměr ČR = 1,00)
const LOCATIONS = [
  { id: "praha", label: "Praha", coef: 1.25 },
  { id: "stredocesky", label: "Středočeský kraj", coef: 1.1 },
  { id: "jihomoravsky", label: "Jihomoravský kraj", coef: 1.08 },
  { id: "plzensky", label: "Plzeňský kraj", coef: 1.03 },
  { id: "kralovehradecky", label: "Královéhradecký kraj", coef: 1.0 },
  { id: "ustecky", label: "Ústecký kraj", coef: 0.97 },
  { id: "liberecky", label: "Liberecký kraj", coef: 0.97 },
  { id: "jihocesky", label: "Jihočeský kraj", coef: 0.96 },
  { id: "pardubicky", label: "Pardubický kraj", coef: 0.96 },
  { id: "vysocina", label: "Kraj Vysočina", coef: 0.95 },
  { id: "olomoucky", label: "Olomoucký kraj", coef: 0.95 },
  { id: "zlinsky", label: "Zlínský kraj", coef: 0.94 },
  { id: "moravskoslezsky", label: "Moravskoslezský kraj", coef: 0.93 },
  { id: "karlovarsky", label: "Karlovarský kraj", coef: 0.92 },
];

const GARAGE_PRICE = 300000;
const CELLAR_PRICE = 250000;

// Podíl práce na celkové ceně (zbytek = materiál)
const LABOR_SHARE = { novostavba: 0.45, "rek-domu": 0.5, "rek-bytu": 0.5 };

const fmt = (n) =>
  `${new Intl.NumberFormat("cs-CZ").format(Math.round(n / 10000) * 10000)} Kč`;

export default function Calculator() {
  const [type, setType] = useState("novostavba");
  const [location, setLocation] = useState("praha");
  const [area, setArea] = useState(90);
  const [garage, setGarage] = useState(false);
  const [cellar, setCellar] = useState(false);
  const [floors, setFloors] = useState(1);
  const [attic, setAttic] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [parseText, setParseText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [analysis, setAnalysis] = useState("");
  const [analyzing, setAnalyzing] = useState(false);

  const prefillFromText = async () => {
    const text = parseText.trim();
    if (text.length < 5 || parsing) return;
    setParsing(true);
    try {
      const res = await axios.post(`${API}/project/parse`, { text });
      const d = res.data || {};
      if (d.type) setType(d.type);
      if (d.area) setArea(d.area);
      if (d.location_id) setLocation(d.location_id);
      if (d.type === "novostavba" || (!d.type && type === "novostavba")) {
        if (d.floors) setFloors(d.floors);
        if (typeof d.attic === "boolean") setAttic(d.attic);
      }
      if (typeof d.garage === "boolean") setGarage(d.garage);
      if (typeof d.cellar === "boolean") setCellar(d.cellar);
      if (Object.keys(d).length === 0) {
        toast.info("Z popisu jsem nic nerozpoznal — zkuste ho upřesnit (typ, m², město).");
      } else {
        toast.success("Kalkulačka předvyplněná podle vašeho popisu — zkontrolujte a dolaďte.");
      }
    } catch {
      toast.error("Popis se nepodařilo rozebrat. Zkuste to prosím znovu nebo vyplňte ručně.");
    } finally {
      setParsing(false);
    }
  };

  const runAnalysis = async () => {
    const text = form.message.trim();
    if (text.length < 10 || analyzing) return;
    setAnalysis("");
    setAnalyzing(true);
    try {
      await streamSSE("/project/analyze", { text }, (delta) =>
        setAnalysis((prev) => prev + delta)
      );
    } catch {
      setAnalysis("Rozbor se nepodařil, zkuste to prosím znovu.");
    } finally {
      setAnalyzing(false);
    }
  };

  const isHouse = type !== "rek-bytu";

  const calc = useMemo(() => {
    const t = TYPES.find((x) => x.id === type);
    const l = LOCATIONS.find((x) => x.id === location);
    const a = Math.min(400, Math.max(30, area || 30));
    const shape = type === "novostavba" ? FLOORS_COEF[floors] * (attic ? ATTIC_COEF : 1) : 1;
    const extras = (isHouse && garage ? GARAGE_PRICE : 0) + (isHouse && cellar ? CELLAR_PRICE : 0);
    const areaPart = t.rate * a * l.coef * shape;
    const base = areaPart + extras;
    const labor = areaPart * LABOR_SHARE[type] + extras * 0.4;
    const material = base - labor;
    return {
      base,
      low: base * 0.9,
      high: base * 1.12,
      rate: Math.round(t.rate * l.coef * shape),
      area: a,
      labor,
      material,
      extras,
    };
  }, [type, location, area, garage, cellar, floors, attic, isHouse]);

  const summary = () => {
    const t = TYPES.find((x) => x.id === type);
    const l = LOCATIONS.find((x) => x.id === location);
    const lines = [
      `Typ projektu: ${t.label}`,
      `Lokalita: ${l.label}`,
      `Podlahová plocha: ${Math.min(400, Math.max(30, area || 30))} m²`,
    ];
    if (type === "novostavba") {
      lines.push(`Počet pater: ${floors}`, `Obytné podkroví: ${attic ? "ano" : "ne"}`);
    }
    if (isHouse) {
      lines.push(`Garáž: ${garage ? "ano" : "ne"}`, `Sklep: ${cellar ? "ano" : "ne"}`);
    }
    lines.push(`Orientační odhad: ${fmt(calc.low)} – ${fmt(calc.high)}`);
    return lines.join("\n");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: `${summary()}${form.message ? `\n\nPoznámka klienta:\n${form.message}` : ""}${analysis ? `\n\n--- AI rozbor zakázky ---\n${analysis}` : ""}`,
        form_type: "kalkulacka",
      });
      toast.success("Poptávka odeslána. Ozveme se vám do 24 hodin.");
      setForm({ name: "", email: "", phone: "", message: "" });
      setFormOpen(false);
    } catch {
      toast.error("Odeslání se nezdařilo. Zkuste to prosím znovu, nebo volejte +420 777 940 118.");
    } finally {
      setSending(false);
    }
  };

  const pill = (active) =>
    `px-5 py-3 rounded-lg border text-sm font-medium transition-colors duration-300 text-left ${
      active
        ? "border-gold bg-gold/10 text-gold-light"
        : "border-white/10 text-zinc-400 hover:border-gold/40 hover:text-zinc-200"
    }`;

  return (
    <section id="kalkulacka" data-testid="calculator-section" className="py-24 lg:py-32 bg-coal/40">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-16">
        <Reveal>
          <p className="text-xs font-mono tracking-[0.3em] uppercase text-gold/70 mb-4">
            Interaktivní cenová kalkulačka
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-zinc-100 leading-tight">
            Kolik bude stát <span className="gold-text">váš projekt?</span>
          </h2>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-8 max-w-2xl">
            <p className="text-xs font-mono tracking-[0.2em] uppercase text-zinc-500 mb-3">
              Nebo popište projekt vlastními slovy — AI předvyplní kalkulačku
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={parseText}
                onChange={(e) => setParseText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && prefillFromText()}
                maxLength={1500}
                placeholder='Např. „zateplit fasádu domu 120 m² v Brně"'
                data-testid="calc-parse-input"
                className="flex-1 bg-card2 border border-white/10 focus:border-gold/50 rounded-lg px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none transition-colors"
              />
              <button
                type="button"
                onClick={prefillFromText}
                disabled={parsing || parseText.trim().length < 5}
                data-testid="calc-parse-button"
                className="inline-flex items-center justify-center gap-2 border border-gold/40 text-gold hover:bg-gold hover:text-black transition-colors duration-300 rounded-lg px-5 py-3 text-sm font-semibold disabled:opacity-40"
              >
                <Sparkles size={15} />
                {parsing ? "Rozebírám…" : "Předvyplnit"}
              </button>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 bg-card2 border border-gold/15 rounded-2xl p-6 sm:p-10 gold-glow">
            <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-10">
              <div className="space-y-8 min-w-0">
                <div>
                  <p className="text-xs font-mono tracking-[0.2em] uppercase text-zinc-500 mb-3">
                    Typ projektu
                  </p>
                  <div className="flex flex-col gap-2.5">
                    {TYPES.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setType(t.id)}
                        data-testid={`calc-type-${t.id}`}
                        className={`${pill(type === t.id)} flex items-center gap-3`}
                      >
                        <t.icon size={17} /> {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-mono tracking-[0.2em] uppercase text-zinc-500 mb-3">
                    Lokalita (kraj)
                  </p>
                  <div className="relative">
                    <select
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      data-testid="calc-location-select"
                      className="field-input appearance-none pr-10 cursor-pointer"
                      aria-label="Vyberte kraj"
                    >
                      {LOCATIONS.map((l) => (
                        <option key={l.id} value={l.id} className="bg-coal text-zinc-100">
                          {l.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={16}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gold pointer-events-none"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-baseline mb-3">
                    <p className="text-xs font-mono tracking-[0.2em] uppercase text-zinc-500">
                      {type === "novostavba" ? "Celková obytná plocha" : "Podlahová plocha"}
                    </p>
                    <span className="inline-flex items-baseline gap-1.5">
                      <input
                        type="number"
                        min={30}
                        max={400}
                        step={1}
                        value={area}
                        onChange={(e) => setArea(Number(e.target.value))}
                        onBlur={() => setArea((v) => Math.min(400, Math.max(30, v || 30)))}
                        data-testid="calc-area-input"
                        aria-label="Zadejte podlahovou plochu v m²"
                        className="w-24 text-right font-mono text-gold-light text-lg bg-ink/60 border border-white/15 focus:border-gold/60 rounded-lg px-2 py-1 outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <span className="font-mono text-gold-light text-lg" data-testid="calc-area-value">m²</span>
                    </span>
                  </div>
                  <input
                    type="range"
                    min="30"
                    max="400"
                    step="5"
                    value={Math.min(400, Math.max(30, area || 30))}
                    onChange={(e) => setArea(Number(e.target.value))}
                    className="calc-range"
                    style={{ "--fill": `${((Math.min(400, Math.max(30, area || 30)) - 30) / 370) * 100}%` }}
                    data-testid="calc-area-slider"
                    aria-label="Podlahová plocha v m²"
                  />
                  {type === "novostavba" && (
                    <p className="mt-2 text-[11px] text-zinc-600" data-testid="calc-area-hint">
                      Všech pater dohromady — patrový dům 45 + 45 m² zadáte jako 90 m².
                    </p>
                  )}
                  <div className="flex justify-between text-[10px] font-mono text-zinc-600 mt-1.5">
                    <span>30 m²</span>
                    <span>400 m²</span>
                  </div>
                </div>

                {type === "novostavba" && (
                  <div data-testid="calc-shape">
                    <p className="text-xs font-mono tracking-[0.2em] uppercase text-zinc-500 mb-3">
                      Počet pater
                    </p>
                    <div className="grid grid-cols-3 gap-2.5">
                      {[1, 2, 3].map((f) => (
                        <button
                          key={f}
                          type="button"
                          onClick={() => setFloors(f)}
                          data-testid={`calc-floors-${f}`}
                          className={pill(floors === f)}
                        >
                          {f === 1 ? "Přízemí" : `${f} patra`}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => setAttic(!attic)}
                      data-testid="calc-attic-toggle"
                      className={`${pill(attic)} mt-2.5 w-full`}
                    >
                      Obytné podkroví · {attic ? "ano" : "ne"}
                    </button>
                  </div>
                )}

                {isHouse && (
                  <div data-testid="calc-extras">
                    <p className="text-xs font-mono tracking-[0.2em] uppercase text-zinc-500 mb-3">
                      Doplňky
                    </p>
                    <div className="grid grid-cols-2 gap-2.5">
                      <button
                        type="button"
                        onClick={() => setGarage(!garage)}
                        data-testid="calc-garage-toggle"
                        className={pill(garage)}
                      >
                        Garáž · {garage ? "ano" : "ne"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setCellar(!cellar)}
                        data-testid="calc-cellar-toggle"
                        className={pill(cellar)}
                      >
                        Sklep · {cellar ? "ano" : "ne"}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col justify-between bg-ink/60 border border-gold/20 rounded-xl p-7 min-w-0">
                <div>
                  <p className="text-xs font-mono tracking-[0.2em] uppercase text-zinc-500">
                    Orientační odhad
                  </p>
                  <AnimatePresence mode="popLayout">
                    <motion.p
                      key={`${calc.low}-${calc.high}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.35 }}
                      className="mt-3 font-display text-2xl sm:text-3xl lg:text-[1.55rem] lg:whitespace-nowrap gold-text"
                      data-testid="calc-price"
                    >
                      <span className="whitespace-nowrap">{fmt(calc.low)}</span>
                      {" – "}
                      <span className="whitespace-nowrap">{fmt(calc.high)}</span>
                    </motion.p>
                  </AnimatePresence>
                  <p className="mt-3 font-mono text-xs text-zinc-500" data-testid="calc-rate-note">
                    ≈ {new Intl.NumberFormat("cs-CZ").format(calc.rate)} Kč/m² ·{" "}
                    {LOCATIONS.find((x) => x.id === location).label} · střední standard · aliance živnostníků
                  </p>
                  <div className="mt-4 font-mono text-xs text-zinc-400 space-y-1" data-testid="calc-breakdown">
                    <p>
                      <span className="text-zinc-500">Práce</span> ≈ {fmt(calc.labor)}
                    </p>
                    <p>
                      <span className="text-zinc-500">Materiál</span> ≈ {fmt(calc.material)}
                    </p>
                    {calc.extras > 0 && (
                      <p>
                        <span className="text-zinc-500">vč. garáž/sklep</span> ≈ {fmt(calc.extras)}
                      </p>
                    )}
                  </div>
                  <p className="mt-6 text-xs text-zinc-500 leading-relaxed border-l-2 border-gold/40 pl-4" data-testid="calc-disclaimer">
                    Uvedené ceny jsou pouze orientační a nejsou závazné. Každý projekt je zcela
                    unikátní a finální částka závisí na reálném stavu objektu, specifikách pozemku
                    a vašem výběru materiálů. Tento výpočet reflektuje tržní průměr a standardní
                    střední třídu vybavení. Cena je kalkulovaná za práci aliance prověřených
                    živnostníků — bez marží velké stavební firmy.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormOpen(!formOpen)}
                  data-testid="calc-inquiry-toggle"
                  className="mt-8 inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold rounded-full px-6 py-3.5 hover:bg-gold-light transition-colors"
                >
                  <Send size={16} /> Odeslat nezávaznou poptávku
                </button>
              </div>
            </div>

            <AnimatePresence>
              {formOpen && (
                <motion.form
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  onSubmit={submit}
                  data-testid="calc-inquiry-form"
                  className="overflow-hidden"
                >
                  <div className="pt-8 mt-8 border-t border-white/10 grid sm:grid-cols-3 gap-4">
                    <input
                      required
                      placeholder="Jméno a příjmení"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="field-input"
                      data-testid="calc-form-name"
                    />
                    <input
                      required
                      type="email"
                      placeholder="E-mail"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="field-input"
                      data-testid="calc-form-email"
                    />
                    <input
                      placeholder="Telefon"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="field-input"
                      data-testid="calc-form-phone"
                    />
                  </div>
                  <textarea
                    rows="3"
                    placeholder="Poznámka k projektu (nepovinné)"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="field-input mt-4"
                    data-testid="calc-form-message"
                  />
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={runAnalysis}
                      disabled={analyzing || form.message.trim().length < 10}
                      data-testid="calc-form-analysis-button"
                      className="inline-flex items-center gap-2 border border-gold/40 text-gold hover:bg-gold hover:text-black transition-colors duration-300 rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-40"
                    >
                      <Sparkles size={14} />
                      {analyzing ? "Rozebírám zakázku…" : "AI rozbor zakázky"}
                    </button>
                    {form.message.trim().length < 10 && (
                      <span className="ml-3 text-[11px] text-zinc-600">
                        nejdřív stručně popište zakázku v poznámce výše
                      </span>
                    )}
                    {(analysis || analyzing) && (
                      <div
                        data-testid="calc-form-analysis"
                        className="mt-3 bg-ink/60 border border-gold/20 rounded-xl p-4 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap"
                      >
                        {analysis}
                        {analyzing && <span className="inline-block w-2 h-4 bg-gold/70 animate-pulse ml-1" />}
                      </div>
                    )}
                  </div>
                  <div className="mt-4 flex items-center justify-between flex-wrap gap-4">
                    <p className="font-mono text-[11px] text-zinc-600 whitespace-pre-line" data-testid="calc-form-summary">
                      {summary()}
                    </p>
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="calc-form-submit"
                      className="inline-flex items-center gap-2 bg-gold text-black font-semibold rounded-full px-7 py-3 hover:bg-gold-light transition-colors disabled:opacity-50"
                    >
                      {sending ? "Odesílám…" : "Odeslat poptávku"} <Send size={15} />
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => setShowTable(!showTable)}
            data-testid="calc-region-table-toggle"
            className="text-xs font-mono tracking-[0.2em] uppercase text-zinc-500 hover:text-gold transition-colors"
          >
            {showTable ? "Skrýt přehled krajů" : "Přehled koeficientů všech 14 krajů"}
          </button>
          <AnimatePresence>
            {showTable && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.35 }}
                className="overflow-hidden"
                data-testid="calc-region-table"
              >
                <div className="mt-5 bg-card2 border border-white/10 rounded-xl overflow-hidden text-left">
                  <div className="grid grid-cols-3 gap-2 px-5 py-3 border-b border-white/10 text-[10px] font-mono tracking-[0.15em] uppercase text-zinc-500">
                    <span>Kraj</span>
                    <span className="text-center">Koeficient</span>
                    <span className="text-right">≈ Kč/m² ({TYPES.find((x) => x.id === type).label})</span>
                  </div>
                  {LOCATIONS.map((l) => (
                    <div
                      key={l.id}
                      data-testid={`region-row-${l.id}`}
                      className={`grid grid-cols-3 gap-2 px-5 py-2.5 text-sm border-b border-white/5 last:border-0 ${
                        l.id === location ? "bg-gold/10 text-gold-light" : "text-zinc-400"
                      }`}
                    >
                      <span>{l.label}</span>
                      <span className="text-center font-mono">× {l.coef.toFixed(2).replace(".", ",")}</span>
                      <span className="text-right font-mono">
                        {new Intl.NumberFormat("cs-CZ").format(
                          Math.round(TYPES.find((x) => x.id === type).rate * l.coef)
                        )}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[11px] text-zinc-600">
                  Průměr ČR = 1,00. Koeficient reflektuje regionální ceny stavebních prací a materiálu.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
